import { Button, Col, Container, Form, Row } from "react-bootstrap";
import cn from "classnames";
import Search from "@/assets/Search.svg";
import styles from "./styles.module.scss";
import { useCallback, useMemo, useState } from "react";
import { speciality_helper } from "@/helpers/speciality_helper";
import { getRoute } from "@/functions/getRoute";
import { job_type_helper } from "@/helpers/job_type_helper";
import { salary_helper } from "@/helpers/salary_helper";
import { grade_helper } from "@/helpers/grade_helper";

export const QUICK_SEARCH_FIELDS = ["query", "speciality", "grade", "location", "job_type", "salary", "radius"];

const PRESETS = {
  simple: ["query", "speciality", "location"],
  full: QUICK_SEARCH_FIELDS,
  classic: QUICK_SEARCH_FIELDS,
};

const toFilterSlug = (value) =>
  String(value)
    .toLowerCase()
    .replace(/[()[\]{}]/g, "")
    .replace(/[\W\s]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/(^-+|-+$)/g, "");

export const QuickJobSearch = ({ className, stack, variant, fields }) => {
  const [specialities] = useState(speciality_helper.fetch());
  const [job_types] = useState(job_type_helper.fetch());
  const [salaries] = useState(salary_helper.fetch());
  const [grades] = useState(grade_helper.fetch());

  const [query, setQuery] = useState("");
  const [speciality, setSpeciality] = useState("");
  const [grade, setGrade] = useState("");
  const [location, setLocation] = useState("");
  const [job_type, setJobType] = useState([]);
  const [salary, setSalary] = useState("");
  const [radius, setRadius] = useState("5");

  const isClassic = variant === "classic";
  const isSimple = variant === "simple";
  const visibleFields = useMemo(() => {
    const list = fields && fields.length ? fields : PRESETS[variant] || PRESETS.full;
    return QUICK_SEARCH_FIELDS.filter((key) => list.includes(key));
  }, [fields, variant]);

  const shows = useCallback((key) => visibleFields.includes(key), [visibleFields]);

  const handleSubmit = useCallback(
    (e) => {
      if (e && e.preventDefault) e.preventDefault();

      const params = new URLSearchParams();
      if (shows("query") && query) params.set("query", query);
      if (shows("location") && location) params.set("location", location);
      if (shows("radius") && radius) params.set("radius", radius);

      const urlFilters = [];
      if (shows("speciality") && speciality) urlFilters.push(toFilterSlug(speciality));
      if (shows("grade") && grade) urlFilters.push(toFilterSlug(grade));
      if (shows("job_type") && job_type.length) urlFilters.push(...job_type.map(toFilterSlug));
      if (shows("salary") && salary) urlFilters.push(toFilterSlug(salary));

      const search = params.toString();
      let url = getRoute("jobs");
      if (search) url = `${url}?${search}`;
      if (urlFilters.length) url = `${url}#/${urlFilters.join("/")}`;

      window.location.href = url;
    },
    [shows, query, location, radius, speciality, grade, job_type, salary],
  );

  const controls = {
    query: (
      <>
        <Form.Label>{isSimple ? "Profession" : "Job Title / keywords"}</Form.Label>
        <Form.Control
          placeholder={isSimple ? "e.g. Consultant, GP" : "Doctor"}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </>
    ),
    speciality: (
      <>
        <Form.Label>Speciality</Form.Label>
        <Form.Select value={speciality} onChange={(e) => setSpeciality(e.target.value)}>
          <option value="">Select</option>
          {specialities.map(({ title }) => (
            <option key={title} value={title}>
              {title}
            </option>
          ))}
        </Form.Select>
      </>
    ),
    grade: (
      <>
        <Form.Label>Grade</Form.Label>
        <Form.Select value={grade} onChange={(e) => setGrade(e.target.value)}>
          <option value="">Select</option>
          {grades.map(({ title }) => (
            <option key={title} value={title}>
              {title}
            </option>
          ))}
        </Form.Select>
      </>
    ),
    location: (
      <>
        <Form.Label>Location / Postcode</Form.Label>
        <Form.Control placeholder="Sydney" value={location} onChange={(e) => setLocation(e.target.value)} />
      </>
    ),
    salary: (
      <>
        <Form.Label>Salary</Form.Label>
        <Form.Select value={salary} onChange={(e) => setSalary(e.target.value)}>
          <option value="">Select</option>
          {salaries.map(({ title }) => (
            <option key={title} value={title}>
              {title}
            </option>
          ))}
        </Form.Select>
      </>
    ),
    radius: (
      <>
        <Form.Label>Within</Form.Label>
        <Form.Select value={radius} onChange={(e) => setRadius(e.target.value)}>
          <option value="5">5 miles</option>
          <option value="10">10 miles</option>
          <option value="20">20 miles</option>
          <option value="30">30 miles</option>
          <option value="40">40 miles</option>
          <option value="50">50 miles</option>
        </Form.Select>
      </>
    ),
  };

  const jobTypeChecks = job_types.map(({ title }) => (
    <Form.Check
      key={title}
      id={`quick-jobtype-${title}`}
      value={title}
      label={title}
      checked={job_type.includes(title)}
      onChange={(e) => setJobType(e.target.checked ? [...job_type, title] : job_type.filter((i) => i !== title))}
    />
  ));

  const submitButton = (
    <Button type="submit" variant="tertiary" className="w-100" onClick={handleSubmit}>
      Search
      <Search />
    </Button>
  );

  if (isClassic) {
    return (
      <div className={cn(className, styles.search)}>
        <Container className="p-0">
          <Form className={cn(styles.search__form)} onSubmit={handleSubmit}>
            <div className="p-4">
              {!stack && <h3 className="mb-3">Find your next job</h3>}
              <Row>
                <Col xs={12} md={stack ? 12 : 3} className="mb-2">
                  <Form.Group>{controls.query}</Form.Group>
                </Col>
                <Col xs={12} md={stack ? 12 : 3} className="mb-2">
                  <Form.Group>{controls.speciality}</Form.Group>
                </Col>
                <Col xs={12} md={stack ? 12 : 3} className="mb-2">
                  <Form.Group>{controls.grade}</Form.Group>
                </Col>
                <Col xs={12} md={stack ? 12 : 3} className="mb-2">
                  <Form.Group>{controls.location}</Form.Group>
                </Col>
                <Col xs={12} md={stack ? 12 : 3} className="mb-2">
                  <Form.Group>
                    <Form.Label>Job type</Form.Label>
                    {jobTypeChecks}
                  </Form.Group>
                </Col>
                {!stack && (
                  <>
                    <Col xs={6} md={2} className="mb-2">
                      <Form.Group>{controls.salary}</Form.Group>
                    </Col>
                    <Col xs={12} md={2} className="mb-3">
                      <Form.Group>{controls.radius}</Form.Group>
                    </Col>
                    <Col xs={12} md={2} />
                  </>
                )}
                <Col xs={12} md={stack ? 12 : 3}>
                  <Form.Label className="opacity-0">Submit</Form.Label>
                  {submitButton}
                </Col>
              </Row>
            </div>
          </Form>
        </Container>
      </div>
    );
  }

  return (
    <div className={cn(className, styles.search, isSimple && styles["search--simple"])}>
      <Container className="p-0">
        <Form className={cn(styles.search__form)} onSubmit={handleSubmit}>
          <div className="p-4">
            {!stack && <h3 className="mb-3">Find your next job</h3>}
            <div className={cn(styles.search__fields, stack && styles["search__fields--stack"])}>
              {shows("query") && <Form.Group className={styles.search__field}>{controls.query}</Form.Group>}
              {shows("speciality") && <Form.Group className={styles.search__field}>{controls.speciality}</Form.Group>}
              {shows("grade") && <Form.Group className={styles.search__field}>{controls.grade}</Form.Group>}
              {shows("location") && <Form.Group className={styles.search__field}>{controls.location}</Form.Group>}
              {shows("job_type") && (
                <Form.Group className={cn(styles.search__field, styles["search__field--checks"])}>
                  <Form.Label className="d-block mb-2">Job type</Form.Label>
                  <div className={styles.search__checks}>{jobTypeChecks}</div>
                </Form.Group>
              )}
              {shows("salary") && (
                <Form.Group className={cn(styles.search__field, styles["search__field--sm"])}>
                  {controls.salary}
                </Form.Group>
              )}
              {shows("radius") && (
                <Form.Group className={cn(styles.search__field, styles["search__field--sm"])}>
                  {controls.radius}
                </Form.Group>
              )}
              <div className={styles.search__submit}>{submitButton}</div>
            </div>
          </div>
        </Form>
      </Container>
    </div>
  );
};

QuickJobSearch.defaultProps = {
  className: "",
  stack: false,
  variant: "classic",
  fields: null,
};

export default QuickJobSearch;
